/**
 * ParticleSystem.ts - Particle effects for battle animations
 * Handles sparkles, flames, ice shards, blood/hit particles, and magic gathering
 */

export interface Particle {
  x: number;
  y: number;
  vx: number; // Velocity X
  vy: number; // Velocity Y
  lifetime: number; // Total lifetime in ms
  age: number; // Current age in ms
  size: number;
  color: string;
  alpha: number;
  rotation?: number;
  rotationSpeed?: number;
  gravity?: number;
}

export interface ParticleEmitterConfig {
  x: number;
  y: number;
  particleCount: number;
  particleLife: number; // Lifetime in ms
  velocityRange: { min: number; max: number };
  angleRange?: { min: number; max: number }; // In radians
  sizeRange: { min: number; max: number };
  colors: string[]; // Array of possible colors
  gravity?: number; // Gravity acceleration
  burst?: boolean; // Emit all at once vs over time
  fadeOut?: boolean; // Fade out at end of life
  rotation?: boolean; // Enable rotation
}

export class ParticleSystem {
  private particles: Particle[] = [];

  /**
   * Emit particles with specified configuration
   */
  emit(config: ParticleEmitterConfig): void {
    const {
      x,
      y,
      particleCount,
      particleLife,
      velocityRange,
      angleRange,
      sizeRange,
      colors,
      gravity = 0,
      burst = true,
      fadeOut = true,
      rotation = false,
    } = config;

    for (let i = 0; i < particleCount; i++) {
      // Random angle
      const angle = angleRange
        ? angleRange.min + Math.random() * (angleRange.max - angleRange.min)
        : Math.random() * Math.PI * 2;

      // Random velocity
      const velocity =
        velocityRange.min + Math.random() * (velocityRange.max - velocityRange.min);

      // Random size
      const size = sizeRange.min + Math.random() * (sizeRange.max - sizeRange.min);

      // Random color
      const color = colors[Math.floor(Math.random() * colors.length)] ?? '#ffffff';

      const particle: Particle = {
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        lifetime: particleLife,
        age: 0,
        size,
        color,
        alpha: 1,
        gravity,
        rotation: rotation ? Math.random() * Math.PI * 2 : undefined,
        rotationSpeed: rotation ? (Math.random() - 0.5) * 0.1 : undefined,
      };

      this.particles.push(particle);
    }
  }

  /**
   * Emit sparkle particles (rising, twinkling)
   */
  emitSparkles(x: number, y: number, count: number = 10, color?: string): void {
    this.emit({
      x,
      y,
      particleCount: count,
      particleLife: 800,
      velocityRange: { min: 0.02, max: 0.08 },
      angleRange: { min: -Math.PI / 2 - 0.5, max: -Math.PI / 2 + 0.5 }, // Mostly upward
      sizeRange: { min: 2, max: 4 },
      colors: color ? [color] : ['#ffff00', '#ffffff', '#ffdd00'],
      gravity: -0.0001, // Slight upward drift
      fadeOut: true,
    });
  }

  /**
   * Emit flame particles (rising with flicker)
   */
  emitFlames(x: number, y: number, count: number = 15): void {
    this.emit({
      x,
      y,
      particleCount: count,
      particleLife: 600,
      velocityRange: { min: 0.05, max: 0.15 },
      angleRange: { min: -Math.PI / 2 - 0.8, max: -Math.PI / 2 + 0.8 }, // Wide cone upward
      sizeRange: { min: 3, max: 8 },
      colors: ['#ff4400', '#ff8800', '#ffaa00', '#ffff00'],
      gravity: -0.0002, // Rise faster
      fadeOut: true,
    });
  }

  /**
   * Emit ice shards (exploding outward)
   */
  emitIceShards(x: number, y: number, count: number = 12): void {
    this.emit({
      x,
      y,
      particleCount: count,
      particleLife: 500,
      velocityRange: { min: 0.1, max: 0.25 },
      angleRange: { min: 0, max: Math.PI * 2 }, // All directions
      sizeRange: { min: 2, max: 5 },
      colors: ['#aaddff', '#ccffff', '#ffffff'],
      gravity: 0.0003, // Fall down
      fadeOut: true,
      rotation: true,
    });
  }

  /**
   * Emit blood/hit particles (short burst on impact)
   */
  emitHitParticles(x: number, y: number, count: number = 8): void {
    this.emit({
      x,
      y,
      particleCount: count,
      particleLife: 400,
      velocityRange: { min: 0.08, max: 0.2 },
      angleRange: { min: 0, max: Math.PI * 2 },
      sizeRange: { min: 2, max: 4 },
      colors: ['#ff0000', '#cc0000', '#ffffff'],
      gravity: 0.0005, // Quick fall
      fadeOut: true,
    });
  }

  /**
   * Emit magic gathering particles (converging to point)
   */
  emitMagicGathering(x: number, y: number, count: number = 20, color?: string): void {
    // Emit particles in a circle that will move inward
    const radius = 40;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const startX = x + Math.cos(angle) * radius;
      const startY = y + Math.sin(angle) * radius;

      // Velocity toward center
      const velocity = 0.15;
      const particleColor = color || ['#88ffff', '#ff88ff', '#ffff88'][Math.floor(Math.random() * 3)];

      this.particles.push({
        x: startX,
        y: startY,
        vx: -Math.cos(angle) * velocity,
        vy: -Math.sin(angle) * velocity,
        lifetime: 800,
        age: 0,
        size: 3,
        color: particleColor ?? '#ffffff',
        alpha: 1,
        gravity: 0,
      });
    }
  }

  /**
   * Emit lightning sparks (electric discharge)
   */
  emitLightningSparks(x: number, y: number, count: number = 15): void {
    this.emit({
      x,
      y,
      particleCount: count,
      particleLife: 300,
      velocityRange: { min: 0.15, max: 0.3 },
      angleRange: { min: 0, max: Math.PI * 2 },
      sizeRange: { min: 1, max: 3 },
      colors: ['#ffff00', '#ffffff', '#aaffff'],
      gravity: 0,
      fadeOut: true,
    });
  }

  /**
   * Emit healing sparkles (green, rising gently)
   */
  emitHealSparkles(x: number, y: number, count: number = 12): void {
    this.emit({
      x,
      y,
      particleCount: count,
      particleLife: 1000,
      velocityRange: { min: 0.02, max: 0.06 },
      angleRange: { min: -Math.PI / 2 - 0.3, max: -Math.PI / 2 + 0.3 },
      sizeRange: { min: 2, max: 5 },
      colors: ['#00ff00', '#44ff44', '#88ff88', '#aaffaa'],
      gravity: -0.00005, // Very gentle rise
      fadeOut: true,
    });
  }

  /**
   * Emit poison bubbles (purple, floating)
   */
  emitPoisonBubbles(x: number, y: number, count: number = 5): void {
    this.emit({
      x,
      y,
      particleCount: count,
      particleLife: 1500,
      velocityRange: { min: 0.01, max: 0.03 },
      angleRange: { min: -Math.PI / 2 - 0.5, max: -Math.PI / 2 + 0.5 },
      sizeRange: { min: 4, max: 8 },
      colors: ['#884488', '#aa44aa', '#cc44cc'],
      gravity: -0.00003, // Very slow rise
      fadeOut: true,
    });
  }

  /**
   * Update all particles
   */
  update(deltaTime: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      if (!particle) continue;

      // Age the particle
      particle.age += deltaTime;

      // Remove if dead
      if (particle.age >= particle.lifetime) {
        this.particles.splice(i, 1);
        continue;
      }

      // Update position
      particle.x += particle.vx * deltaTime;
      particle.y += particle.vy * deltaTime;

      // Apply gravity
      if (particle.gravity !== undefined) {
        particle.vy += particle.gravity * deltaTime;
      }

      // Update rotation
      if (particle.rotation !== undefined && particle.rotationSpeed !== undefined) {
        particle.rotation += particle.rotationSpeed * deltaTime;
      }

      // Update alpha (fade out)
      const lifeProgress = particle.age / particle.lifetime;
      if (lifeProgress > 0.7) {
        // Fade out in last 30% of life
        particle.alpha = 1 - (lifeProgress - 0.7) / 0.3;
      }
    }
  }

  /**
   * Render all particles
   */
  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    for (const particle of this.particles) {
      ctx.globalAlpha = particle.alpha;

      if (particle.rotation !== undefined) {
        // Render with rotation
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate(particle.rotation);
        ctx.fillStyle = particle.color;
        ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);
        ctx.restore();
      } else {
        // Render simple circle/square
        ctx.fillStyle = particle.color;
        ctx.fillRect(
          particle.x - particle.size / 2,
          particle.y - particle.size / 2,
          particle.size,
          particle.size
        );
      }
    }

    ctx.restore();
  }

  /**
   * Clear all particles
   */
  clear(): void {
    this.particles = [];
  }

  /**
   * Get particle count
   */
  getCount(): number {
    return this.particles.length;
  }

  /**
   * Check if any particles are active
   */
  hasParticles(): boolean {
    return this.particles.length > 0;
  }
}
